/**
 * EditorToolbar
 *
 * The editor's chrome lives here. The file exposes three slot-ready
 * sub-components used by the new notch-based ReportEditorLayout:
 *
 *   - <EditorActionsLeft />  → Close · Preview · Share · save status · title
 *   - <EditorSearchTrigger /> → ⌘K trigger (dispatches a synthetic Cmd+K)
 *   - <EditorActionsRight /> → Sources pill · Schedule pill
 *
 * The legacy <EditorToolbar /> remains exported as a flex 56px header
 * that composes the three sub-components, so older tests and any callers
 * that still mount the monolithic toolbar keep working.
 */

import { useState, useRef, useCallback, useEffect, useMemo, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Eye,
  CalendarDots,
  CaretDown,
  ShareNetwork,
  Link,
  FilePdf,
  EnvelopeSimple,
  PresentationChart,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Button,
  Calendar,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Input,
} from "@provenleap/ui";
import {
  DATE_RANGE_PRESETS,
  COMPARISON_MODE_LABELS,
} from "../../lib/dateRangeUtils";
import type { DateRangeState } from "../../hooks/useDateRange";
import type { DateRangePresetKey, ComparisonMode } from "../../lib/dateRangeUtils";
import type { DateRange as DayPickerDateRange } from "react-day-picker";

export type SaveStatus = "saved" | "saving" | "unsaved";

// ── Shared prop types ──────────────────────────────────────────────────

export interface EditorActionsLeftProps {
  onPreview: () => void;
  onClose?: () => void;
  /** When true, the Close (X) button is rendered AFTER Preview/Share instead of before. */
  closeLast?: boolean;
}

export interface EditorActionsRightProps {
  dateRangeState: DateRangeState;
  onSelectPreset: (key: DateRangePresetKey) => void;
  onSelectCustomRange: (start: string, end: string) => void;
  onToggleComparison: () => void;
  onSetComparisonMode: (mode: ComparisonMode) => void;
  connectedIntegrations?: string[];
  clientId?: string;
}

/** Combined props for the legacy <EditorToolbar /> wrapper. */
export interface EditorToolbarProps extends EditorActionsLeftProps, EditorActionsRightProps {
  reportName: string;
  onReportNameChange: (name: string) => void;
  saveStatus: SaveStatus;
  onClose: () => void;
  clientName?: string;
  leftSidebarVisible?: boolean;
  rightSidebarVisible?: boolean;
}

const SAVE_STATUS_LABELS: Record<SaveStatus, string> = {
  saved: "Auto saved",
  saving: "Saving...",
  unsaved: "Unsaved changes",
};

const SAVE_STATUS_DOT_CLASSES: Record<SaveStatus, string> = {
  saved: "bg-muted-foreground/30",
  saving: "bg-muted-foreground/60 animate-pulse",
  unsaved: "bg-primary",
};

const SAVE_STATUS_LABEL_CLASSES: Record<SaveStatus, string> = {
  saved: "text-foreground",
  saving: "text-foreground",
  unsaved: "text-foreground",
};

// ── Date helpers ───────────────────────────────────────────────────────

function formatDateDisplay(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00Z");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Splits a compact range into the date portion and a trailing year, so the year
 * can be rendered in a lighter color: { range: "Apr 22 – May 22", year: "2026" }.
 * When the two dates span different years, year is "" and range holds both full dates.
 */
function formatDateRangeParts(start: string, end: string): { range: string; year: string } {
  if (!start || !end) return { range: "", year: "" };
  const s = new Date(start + "T00:00:00Z");
  const e = new Date(end + "T00:00:00Z");
  const sameYear = s.getUTCFullYear() === e.getUTCFullYear();
  if (!sameYear) {
    return { range: `${formatDateDisplay(start)} – ${formatDateDisplay(end)}`, year: "" };
  }
  const monthDay = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  return { range: `${monthDay(s)} – ${monthDay(e)}`, year: String(e.getUTCFullYear()) };
}

function toUTCDate(dateStr: string): Date {
  const parts = dateStr.split("-");
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
}

function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// ── Tooltip shared by left-cluster icon buttons ────────────────────────

function ToolbarTooltip({ label, children }: { label: string; children: ReactNode }) {
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const show = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setTooltipPos({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
  }, []);

  const hide = useCallback(() => setTooltipPos(null), []);

  return (
    <div ref={ref} onMouseEnter={show} onMouseLeave={hide} className="relative">
      {children}
      {tooltipPos &&
        createPortal(
          <div
            className="fixed z-[100] pointer-events-none"
            style={{ top: tooltipPos.top, left: tooltipPos.left, transform: "translateX(-50%)" }}
          >
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-background border-l border-t border-input rotate-45 z-10" />
            <div className="relative bg-background text-foreground text-xs font-medium px-2.5 py-1 rounded-md border border-input shadow-md whitespace-nowrap">
              {label}
            </div>
          </div>,
          document.getElementById("authenticated-root") ?? document.body,
        )}
    </div>
  );
}

// ── Left cluster: Close / Preview / Share + inline title ──────────────

export function EditorActionsLeft({ onPreview, onClose, closeLast = false }: EditorActionsLeftProps) {
  const closeBtn = onClose ? (
    <ToolbarTooltip label="Close">
      <Button
        variant="ghost"
        size="sm"
        onClick={onClose}
        data-testid="editor-close-btn"
        aria-label="Close editor"
        className="p-0 !text-foreground hover:!text-foreground hover:bg-accent/50 rounded-lg cursor-pointer [&_svg]:size-[16.5px]"
        style={{ width: 35, height: 35 }}
      >
        <X size={16.5} />
      </Button>
    </ToolbarTooltip>
  ) : null;

  return (
    <div data-testid="editor-actions-left" className="flex items-center gap-2">
      {!closeLast && closeBtn}

      <ToolbarTooltip label="Preview">
        <Button
          variant="ghost"
          size="sm"
          onClick={onPreview}
          data-testid="editor-preview-btn"
          aria-label="Preview"
          className="p-0 !text-foreground hover:!text-foreground hover:bg-accent/50 rounded-lg cursor-pointer [&_svg]:size-[16.5px]"
          style={{ width: 35, height: 35 }}
        >
          <Eye size={16.5} />
        </Button>
      </ToolbarTooltip>

      <ToolbarTooltip label="Share">
        <SharePopover />
      </ToolbarTooltip>

      {closeLast && closeBtn}
    </div>
  );
}


// ── Save status — brand-red dot + label (placed under the sidebar logo) ─

export function EditorSaveStatus({ saveStatus }: { saveStatus: SaveStatus }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${SAVE_STATUS_DOT_CLASSES[saveStatus]}`} />
      <span
        className={`text-[12px] whitespace-nowrap ${SAVE_STATUS_LABEL_CLASSES[saveStatus]}`}
        data-testid="editor-save-status"
      >
        {SAVE_STATUS_LABELS[saveStatus]}
      </span>
    </div>
  );
}

// ── Report title — editable, lives above the left Pages sidebar ────────

export function EditorReportTitle({
  reportName,
  onReportNameChange,
  collapsed = false,
}: {
  reportName: string;
  onReportNameChange: (name: string) => void;
  collapsed?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(reportName);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!isEditing && draft !== reportName) setDraft(reportName);

  const startEdit = () => {
    if (collapsed) return;
    setDraft(reportName);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== reportName) onReportNameChange(trimmed);
    else setDraft(reportName);
    setIsEditing(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setDraft(reportName);
      setIsEditing(false);
    }
  };

  return (
    <div
      className="overflow-hidden"
      style={{
        height: collapsed ? 0 : "auto",
        opacity: collapsed ? 0 : 1,
        transition: "opacity 0.2s ease",
      }}
    >
      <div className="flex justify-center">
        {isEditing ? (
          <Input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, 100))}
            onBlur={commit}
            onKeyDown={onKeyDown}
            maxLength={100}
            className="h-6 text-[13.5px] font-medium text-center px-2 py-0 min-w-[160px]"
            data-testid="editor-name-input"
          />
        ) : (
          <button
            type="button"
            onClick={startEdit}
            className="max-w-[240px] text-center text-[13.5px] font-medium text-foreground truncate rounded-lg px-2 py-1 hover:bg-accent/40 transition-colors cursor-text"
            data-testid="editor-name-display"
            title="Click to rename"
          >
            {reportName}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Center: Cmd+K search trigger ───────────────────────────────────────

export function EditorSearchTrigger() {
  const openPalette = () => {
    // WidgetCommandPalette listens globally for Cmd+K in capture phase
    const ev = new KeyboardEvent("keydown", {
      key: "k",
      metaKey: true,
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(ev);
  };

  return (
    <ToolbarTooltip label="Search">
      <Button
        variant="ghost"
        size="sm"
        onClick={openPalette}
        data-testid="editor-search-trigger"
        aria-label="Search widgets (⌘K)"
        className="p-0 !text-foreground hover:!text-foreground hover:bg-accent/50 rounded-lg cursor-pointer [&_svg]:size-[16.5px]"
        style={{ width: 35, height: 35 }}
      >
        <MagnifyingGlass size={16.5} />
      </Button>
    </ToolbarTooltip>
  );
}

// ── Right cluster: Sources pill + Schedule pill ────────────────────────

export function EditorActionsRight({
  dateRangeState,
  onSelectPreset,
  onSelectCustomRange,
  onToggleComparison,
  onSetComparisonMode,
}: EditorActionsRightProps) {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const scheduleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scheduleOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (scheduleRef.current && !scheduleRef.current.contains(e.target as Node)) {
        setScheduleOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [scheduleOpen]);

  const calendarRange: DayPickerDateRange | undefined = useMemo(() => {
    if (!dateRangeState.start || !dateRangeState.end) return undefined;
    return {
      from: toUTCDate(dateRangeState.start),
      to: toUTCDate(dateRangeState.end),
    };
  }, [dateRangeState.start, dateRangeState.end]);

  const handleCalendarSelect = useCallback(
    (range: DayPickerDateRange | undefined) => {
      if (!range?.from) return;
      const start = toISODate(range.from);
      const end = range.to ? toISODate(range.to) : start;
      onSelectCustomRange(start, end);
    },
    [onSelectCustomRange],
  );

  const currentPreset = DATE_RANGE_PRESETS.find((p) => p.key === dateRangeState.presetKey);
  const presetLabel = currentPreset?.label ?? "Custom";
  // Compact range without the year (year intentionally omitted from the button).
  const { range: buttonDateDisplay } = formatDateRangeParts(
    dateRangeState.start,
    dateRangeState.end,
  );

  return (
    <div className="flex items-center gap-2 shrink-0 relative">
      {/* Sources pill removed from the notch — Connected Sources lives in the
          left sidebar footer; the top pill was a redundant duplicate. */}

      <div className="relative" ref={scheduleRef}>
        <Button
          variant="ghost"
          onClick={() => setScheduleOpen((v) => !v)}
          data-testid="editor-schedule-btn"
          className="shrink-0 rounded-2xl text-[13.5px] !text-foreground hover:!text-foreground cursor-pointer hover:bg-accent/50 transition-colors border-0"
        >
          <span className="inline-flex items-center gap-1.5 font-normal text-foreground">
            <CalendarDots size={19} className="text-foreground shrink-0" />
            <span className="hidden sm:inline">{buttonDateDisplay || presetLabel}</span>
          </span>
          <CaretDown size={11} className="ml-1" />
        </Button>

        <AnimatePresence>
          {scheduleOpen && (
            <>
              <div className="fixed inset-0 z-[55]" onClick={() => setScheduleOpen(false)} />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -4 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="absolute left-0 top-full mt-2.5 z-[60] bg-popover rounded-2xl shadow-md"
                style={{ border: "0.5px solid var(--hairline)" }}
                data-testid="schedule-popover"
              >
                <div
                  className="absolute -top-[5px] left-6 w-2.5 h-2.5 bg-popover rotate-45 z-10"
                  style={{
                    borderLeft: "0.5px solid var(--hairline)",
                    borderTop: "0.5px solid var(--hairline)",
                  }}
                />
                <div className="flex">
                  <div className="p-4 border-r border-border" data-testid="editor-date-range">
                    <Calendar
                      mode="range"
                      selected={calendarRange}
                      onSelect={handleCalendarSelect}
                      numberOfMonths={2}
                      disabled={{ after: new Date() }}
                      data-testid="schedule-calendar"
                    />
                  </div>

                  <div className="w-64 p-5 flex flex-col gap-5">
                    <div>
                      <p className="text-[13px] font-semibold text-foreground mb-2">Date Range</p>
                      <Select
                        value={dateRangeState.presetKey ?? "custom"}
                        onValueChange={(v) => onSelectPreset(v as DateRangePresetKey)}
                      >
                        <SelectTrigger
                          className="w-full rounded-lg text-[13px] shadow-xs"
                          style={{ border: "0.5px solid var(--hairline)" }}
                          data-testid="preset-trigger"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DATE_RANGE_PRESETS.map((preset) => (
                            <SelectItem
                              key={preset.key}
                              value={preset.key}
                              data-testid={`preset-${preset.key}`}
                              className="text-[13px] cursor-pointer rounded-md"
                            >
                              {preset.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div
                        className="rounded-lg px-3 py-2 text-[13px] text-foreground shadow-xs bg-background"
                        style={{ border: "0.5px solid var(--hairline)" }}
                        data-testid="date-start-display"
                      >
                        {formatDateDisplay(dateRangeState.start)}
                      </div>
                      <div
                        className="rounded-lg px-3 py-2 text-[13px] text-foreground shadow-xs bg-background"
                        style={{ border: "0.5px solid var(--hairline)" }}
                        data-testid="date-end-display"
                      >
                        {formatDateDisplay(dateRangeState.end)}
                      </div>
                    </div>

                    <div>
                      <p className="text-[13px] font-semibold text-foreground mb-2">Compare To</p>
                      <Select
                        value={dateRangeState.comparisonMode}
                        onValueChange={(v) => onSetComparisonMode(v as ComparisonMode)}
                        disabled={!dateRangeState.comparisonEnabled}
                      >
                        <SelectTrigger
                          className="w-full rounded-lg text-[13px] shadow-xs"
                          style={{ border: "0.5px solid var(--hairline)" }}
                          data-testid="comparison-mode-trigger"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(
                            Object.entries(COMPARISON_MODE_LABELS) as [ComparisonMode, string][]
                          ).map(([mode, label]) => (
                            <SelectItem
                              key={mode}
                              value={mode}
                              className="text-[13px] cursor-pointer rounded-md"
                            >
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {dateRangeState.comparisonEnabled &&
                      dateRangeState.comparisonStart &&
                      dateRangeState.comparisonEnd && (
                        <div className="grid grid-cols-2 gap-2">
                          <div
                            className="rounded-lg px-3 py-2 text-[13px] text-muted-foreground shadow-xs bg-background"
                            style={{ border: "0.5px solid var(--hairline)" }}
                          >
                            {formatDateDisplay(dateRangeState.comparisonStart)}
                          </div>
                          <div
                            className="rounded-lg px-3 py-2 text-[13px] text-muted-foreground shadow-xs bg-background"
                            style={{ border: "0.5px solid var(--hairline)" }}
                          >
                            {formatDateDisplay(dateRangeState.comparisonEnd)}
                          </div>
                        </div>
                      )}

                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-semibold text-foreground">
                        Enable comparison
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={dateRangeState.comparisonEnabled}
                        onClick={() => onToggleComparison()}
                        data-testid="comparison-toggle"
                        className={`relative shrink-0 rounded-full transition-colors cursor-pointer p-0 border-0 ${
                          dateRangeState.comparisonEnabled ? "bg-primary" : "bg-muted"
                        }`}
                        style={{
                          width: 36,
                          height: 20,
                          minWidth: 36,
                          maxWidth: 36,
                          minHeight: 20,
                          maxHeight: 20,
                          display: "inline-block",
                        }}
                      >
                        <span
                          aria-hidden
                          className="absolute bg-white shadow-sm rounded-full transition-transform"
                          style={{
                            width: 16,
                            height: 16,
                            top: 2,
                            left: dateRangeState.comparisonEnabled ? 18 : 2,
                          }}
                        />
                      </button>
                    </div>

                    <div className="flex gap-2 mt-auto pt-2">
                      <Button
                        variant="brand"
                        className="flex-1 rounded-2xl text-[13px] cursor-pointer"
                        onClick={() => setScheduleOpen(false)}
                        data-testid="schedule-apply"
                      >
                        Apply
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
                        style={{ border: "0.5px solid var(--hairline)" }}
                        onClick={() => setScheduleOpen(false)}
                        data-testid="schedule-cancel"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ── Legacy compat: <EditorToolbar /> as a flat 56px header ─────────────

export function EditorToolbar(props: EditorToolbarProps) {
  return (
    <header
      data-testid="editor-toolbar"
      className="relative shrink-0 bg-background flex items-center px-4 gap-2"
      style={{ height: 56 }}
    >
      <EditorActionsLeft onPreview={props.onPreview} onClose={props.onClose} />
      <EditorSearchTrigger />
      <EditorSaveStatus saveStatus={props.saveStatus} />
      <div className="flex-1 flex justify-center">
        <div className="max-w-[380px]">
          <EditorReportTitle
            reportName={props.reportName}
            onReportNameChange={props.onReportNameChange}
          />
        </div>
      </div>
      <EditorActionsRight
        dateRangeState={props.dateRangeState}
        onSelectPreset={props.onSelectPreset}
        onSelectCustomRange={props.onSelectCustomRange}
        onToggleComparison={props.onToggleComparison}
        onSetComparisonMode={props.onSetComparisonMode}
        connectedIntegrations={props.connectedIntegrations}
        clientId={props.clientId}
      />
    </header>
  );
}

// ── Share Popover (unchanged in behavior) ──────────────────────────────

const SHARE_OPTIONS = [
  { id: "copy-link", icon: Link, label: "Copy link" },
  { id: "download-pdf", icon: FilePdf, label: "Download PDF" },
  { id: "send-email", icon: EnvelopeSimple, label: "Send via email" },
  { id: "present", icon: PresentationChart, label: "Present" },
] as const;

function SharePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        data-testid="editor-share-btn"
        aria-label="Share"
        title="Share"
        className="p-0 !text-foreground hover:!text-foreground hover:bg-accent/50 rounded-lg cursor-pointer [&_svg]:size-[16.5px]"
        style={{ width: 35, height: 35 }}
      >
        <ShareNetwork size={16.5} />
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="absolute right-0 top-full mt-2 z-50 w-52 rounded-xl border bg-card p-1.5 shadow-xl"
            data-testid="share-popover"
          >
            {SHARE_OPTIONS.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                type="button"
                className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-foreground rounded-lg hover:bg-muted transition-colors"
                data-testid={`share-${id}`}
                onClick={() => setOpen(false)}
              >
                <Icon size={16} className="text-muted-foreground shrink-0" />
                {label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
